import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCros3Form } from "../../../store/slices/cros3FormSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

/* ------------------------------------------------------------------ */
/*  Answer scales — these mirror the exact wording printed on the      */
/*  paper CROS-3.0 form. Keep the option strings identical to what     */
/*  the backend stores in *_responses.qN so the correct circle fills.  */
/* ------------------------------------------------------------------ */
const SCALE = {
  satisfaction: ["Very satisfied", "Somewhat satisfied", "Somewhat dissatisfied", "Very dissatisfied"],
  belief: ["Believe strongly", "Believe", "Believe a little", "Do not believe"],
  frequency: ["All the time", "Most of the time", "Sometimes", "Almost never"],
  progress: ["A great deal", "Some", "Very little", "None at all"],
  coping: [
    "Very well -  never a problem",
    "Fairly well -  rarely a problem",
    "Not so well -  often a problem",
    "Very poorly - always a problem",
  ],
};

// Normalize for comparison so minor spacing/casing differences from the
// backend don't prevent the correct circle from filling in.
const norm = (s) => (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

/* ------------------------------------------------------------------ */
/*  Question text — Part 1: Consumer CROS 3.0                          */
/* ------------------------------------------------------------------ */
const CONSUMER_QUESTIONS = [
  { header: "Think about your satisfaction with services:" },
  { num: 1, text: "How do I feel about the amount of information I receive about my mental illness?", scale: "satisfaction", field: "q1" },
  { num: 2, text: "How do I feel about the choices I get about my care?", scale: "satisfaction", field: "q2" },
  { num: 3, text: "How do I feel about the amount of activities or groups I am offered to help me cope with my mental illness?", scale: "satisfaction", field: "q3" },
  { num: 4, text: "How do I feel about the help I get finding services I need in the community?", scale: "satisfaction", field: "q4" },
  { num: 5, text: "How do I feel about the availability of crisis services when I need them?", scale: "satisfaction", field: "q5" },

  { header: "Think about your hopes for the future:" },
  { num: 6, text: "I believe I will handle my daily problems.", scale: "belief", field: "q6" },
  { num: 7, text: "I believe I will trust my thoughts and feelings.", scale: "belief", field: "q7" },
  { num: 8, text: "I believe I will feel alert and alive.", scale: "belief", field: "q8" },
  { num: 9, text: "I believe I will achieve goals I set for myself.", scale: "belief", field: "q9" },
  { num: 10, text: "I believe my life will have meaning.", scale: "belief", field: "q10" },
  { num: 11, text: "I believe I will have enjoyable experiences.", scale: "belief", field: "q11" },
  { num: 12, text: "I believe I will recover from mental illness.", scale: "belief", field: "q12" },

  { header: "Think about things you do in your daily life:" },
  { num: 13, text: "I plan and keep a daily routine on my own.", scale: "frequency", field: "q13" },
  { num: 14, text: "I concentrate and finish tasks that I start.", scale: "frequency", field: "q14" },
  { num: 15, text: "I make plans to do things with other people.", scale: "frequency", field: "q15" },
  { num: 16, text: "I manage my residence (for example, do chores, pay bills, keep my room/house clean).", scale: "frequency", field: "q16" },
  { num: 17, text: "I make my own decisions.", scale: "frequency", field: "q17" },
  { num: 18, text: "I take care of my personal appearance (for example, bathe, wear clean clothes).", scale: "frequency", field: "q18" },
  { num: 19, text: "I am involved in meaningful activity (for example, employment, school, volunteer work).", scale: "frequency", field: "q19" },
  { num: 20, text: "How much progress am I making in recovering from my mental illness?", scale: "progress", field: "q20" },

  { header: "Think about how well you are coping:" },
  { num: 21, text: "How well am I coping with feeling sad?", scale: "coping", field: "q21" },
  { num: 22, text: "How well am I coping with feeling tense or anxious?", scale: "coping", field: "q22" },
  { num: 23, text: "How well am I coping with feeling angry or hostile toward others?", scale: "coping", field: "q23" },
  { num: 24, text: "How well am I coping with disturbing thoughts?", scale: "coping", field: "q24" },
  { num: 25, text: "How well am I coping with difficulty sleeping?", scale: "coping", field: "q25" },
  { num: 26, text: "How well am I coping with external stressors (for example, health problems, family conflicts, unemployment, tragedy in community)?", scale: "coping", field: "q26" },

  { header: "Think about how satisfied you are with your life:" },
  { num: 27, text: "How do I feel about the amount of freedom I have where I live?", scale: "satisfaction", field: "q27" },
  { num: 28, text: "How do I feel about having things to do that I enjoy (for example, going to the movies, sports events, hobbies)?", scale: "satisfaction", field: "q28" },
  { num: 29, text: "How do I feel about the way I spend my free time?", scale: "satisfaction", field: "q29" },
  { num: 30, text: "How do I feel about the amount of privacy I have where I live?", scale: "satisfaction", field: "q30" },
  { num: 31, text: "How do I feel about the amount of comfort and security I have where I live?", scale: "satisfaction", field: "q31" },
  { num: 32, text: "How do I feel about my access to health care?", scale: "satisfaction", field: "q32" },
  { num: 33, text: "How do I feel about the neighborhood and residence where I live?", scale: "satisfaction", field: "q33" },
  { num: 34, text: "How do I feel about my access to reliable transportation?", scale: "satisfaction", field: "q34" },
  { num: 35, text: "How do I feel about my relationships with family/significant others/friends?", scale: "satisfaction", field: "q35" },

  { header: "Other indicators:" },
  { num: 36, text: "I believe my use of alcohol or street drugs interferes with my recovery from mental illness.", scale: "belief", field: "q36", optOut: "I do not use alcohol or street drugs." },
  { num: 37, text: "I take my psychiatric medication as prescribed.", scale: "frequency", field: "q37", optOut: "Psychiatric medication has not been prescribed for me." },
  { num: 38, text: "I believe taking my psychiatric medication helps with my recovery from mental illness.", scale: "belief", field: "q38", optOut: "Psychiatric medication has not been prescribed for me." },
];

/* ------------------------------------------------------------------ */
/*  Question text — Part 2: Staff CROS 3.0                             */
/*  (Staff & VIP ask "to what extent / how often / how well does the   */
/*  consumer seem..." — note hopes-for-future here uses the frequency  */
/*  scale, not the belief scale used in the Consumer section, matching */
/*  the paper form and the sample response data.)                     */
/* ------------------------------------------------------------------ */
const STAFF_QUESTIONS = [
  { header: "Think about the consumer's hopes for the future:" },
  { num: 6, text: "To what extent does the consumer seem to believe he/she will handle daily problems?", scale: "frequency", field: "q6" },
  { num: 7, text: "To what extent does the consumer seem to believe he/she will trust his/her thoughts and feelings?", scale: "frequency", field: "q7" },
  { num: 8, text: "To what extent does the consumer seem to believe he/she will feel alert and alive?", scale: "frequency", field: "q8" },
  { num: 9, text: "To what extent does the consumer seem to believe he/she will achieve goals he/she sets?", scale: "frequency", field: "q9" },
  { num: 10, text: "To what extent does the consumer seem to believe his/her life will have meaning?", scale: "frequency", field: "q10" },
  { num: 11, text: "To what extent does the consumer seem to believe he/she will have enjoyable experiences?", scale: "frequency", field: "q11" },
  { num: 12, text: "To what extent does the consumer seem to believe he/she will recover from mental illness?", scale: "frequency", field: "q12" },

  { header: "Think about things the consumer does in his/her daily life:" },
  { num: 13, text: "How often does the consumer plan and keep a daily routine on his/her own?", scale: "frequency", field: "q13" },
  { num: 14, text: "How often does the consumer concentrate and finish tasks that he/she starts?", scale: "frequency", field: "q14" },
  { num: 15, text: "How often does the consumer make plans to do things with other people?", scale: "frequency", field: "q15" },
  { num: 16, text: "How often does the consumer manage his/her residence (for example, do chores, pay bills, keep his/her room/house clean)?", scale: "frequency", field: "q16" },
  { num: 17, text: "How often does the consumer make his/her own decisions?", scale: "frequency", field: "q17" },
  { num: 18, text: "How often does the consumer take care of his/her personal appearance (for example, bathe, wear clean clothes)?", scale: "frequency", field: "q18" },
  { num: 19, text: "How often is the consumer involved in meaningful activity (for example, employment, school, volunteer work)?", scale: "frequency", field: "q19" },
  { num: 20, text: "How much progress is the consumer making toward recovering from his/her mental illness?", scale: "progress", field: "q20" },

  { header: "Think about how well the consumer is coping:" },
  { num: 21, text: "How well is the consumer coping with feeling sad?", scale: "coping", field: "q21" },
  { num: 22, text: "How well is the consumer coping with feeling tense or anxious?", scale: "coping", field: "q22" },
  { num: 23, text: "How well is the consumer coping with feeling angry or hostile toward others?", scale: "coping", field: "q23" },
  { num: 24, text: "How well is the consumer coping with disturbing thoughts?", scale: "coping", field: "q24" },
  { num: 25, text: "How well is the consumer coping with difficulty sleeping?", scale: "coping", field: "q25" },
  { num: 26, text: "How well is the consumer coping with external stressors (health problems, family conflicts, unemployment, tragedy in community)?", scale: "coping", field: "q26" },

  { header: "Think about how satisfied the consumer is with his/her life:" },
  { num: 27, text: "How does the consumer feel about the amount of freedom he/she has where he/she lives?", scale: "satisfaction", field: "q27" },
  { num: 28, text: "How does the consumer feel about having things to do that he/she enjoys (for example, going to the movies, sports events, hobbies)?", scale: "satisfaction", field: "q28" },
  { num: 29, text: "How does the consumer feel about the way he/she spends his/her free time?", scale: "satisfaction", field: "q29" },
  { num: 30, text: "How does the consumer feel about the amount of privacy he/she has where he/she lives?", scale: "satisfaction", field: "q30" },
  { num: 31, text: "How does the consumer feel about the amount of comfort and security he/she has where he/she lives?", scale: "satisfaction", field: "q31" },
  { num: 32, text: "How does the consumer feel about his/her access to health care?", scale: "satisfaction", field: "q32" },
  { num: 33, text: "How does the consumer feel about the neighborhood and residence where he/she lives?", scale: "satisfaction", field: "q33" },
  { num: 34, text: "How does the consumer feel about his/her access to reliable transportation?", scale: "satisfaction", field: "q34" },
  { num: 35, text: "How does the consumer feel about his/her relationships with family/significant others/friends?", scale: "satisfaction", field: "q35" },

  { header: "Other indicators:" },
  { num: 36, text: "To what extent do you believe the consumer's use of alcohol or street drugs interferes with his/her recovery from mental illness?", scale: "belief", field: "q36", optOut: "The consumer does not use alcohol or street drugs." },
  { num: 37, text: "To what extent do you believe the consumer takes his/her psychiatric medication as prescribed?", scale: "frequency", field: "q37", optOut: "Psychiatric medication has not been prescribed for the consumer." },
  { num: 38, text: "To what extent do you believe the consumer's psychiatric medication helps with his/her recovery from mental illness?", scale: "belief", field: "q38", optOut: "Psychiatric medication has not been prescribed for the consumer." },
];

/* ------------------------------------------------------------------ */
/*  Question text — Part 3: VIP CROS 3.0 (same wording as Staff,       */
/*  renumbered 1-33)                                                    */
/* ------------------------------------------------------------------ */
const VIP_QUESTIONS = [
  { header: "Think about the consumer's hopes for the future:" },
  { num: 1, text: "To what extent does the consumer seem to believe he/she will handle daily problems?", scale: "frequency", field: "q1" },
  { num: 2, text: "To what extent does the consumer seem to believe he/she will trust his/her thoughts and feelings?", scale: "frequency", field: "q2" },
  { num: 3, text: "To what extent does the consumer seem to believe he/she will feel alert and alive?", scale: "frequency", field: "q3" },
  { num: 4, text: "To what extent does the consumer seem to believe he/she will achieve goals he/she sets?", scale: "frequency", field: "q4" },
  { num: 5, text: "To what extent does the consumer seem to believe his/her life will have meaning?", scale: "frequency", field: "q5" },
  { num: 6, text: "To what extent does the consumer seem to believe he/she will have enjoyable experiences?", scale: "frequency", field: "q6" },
  { num: 7, text: "To what extent does the consumer seem to believe he/she will recover from mental illness?", scale: "frequency", field: "q7" },

  { header: "Think about things the consumer does in his/her daily life:" },
  { num: 8, text: "How often does the consumer plan and keep a daily routine on his/her own?", scale: "frequency", field: "q8" },
  { num: 9, text: "How often does the consumer concentrate and finish tasks that he/she starts?", scale: "frequency", field: "q9" },
  { num: 10, text: "How often does the consumer make plans to do things with other people?", scale: "frequency", field: "q10" },
  { num: 11, text: "How often does the consumer manage his/her residence (for example, do chores, pay bills, keep his/her room/house clean)?", scale: "frequency", field: "q11" },
  { num: 12, text: "How often does the consumer make his/her own decisions?", scale: "frequency", field: "q12" },
  { num: 13, text: "How often does the consumer take care of his/her personal appearance (for example, bathe, wear clean clothes)?", scale: "frequency", field: "q13" },
  { num: 14, text: "How often is the consumer involved in meaningful activity (for example, employment, school, volunteer work)?", scale: "frequency", field: "q14" },
  { num: 15, text: "How much progress is the consumer making toward recovering from his/her mental illness?", scale: "progress", field: "q15" },

  { header: "Think about how well the consumer is coping:" },
  { num: 16, text: "How well is the consumer coping with feeling sad?", scale: "coping", field: "q16" },
  { num: 17, text: "How well is the consumer coping with feeling tense or anxious?", scale: "coping", field: "q17" },
  { num: 18, text: "How well is the consumer coping with feeling angry or hostile toward others?", scale: "coping", field: "q18" },
  { num: 19, text: "How well is the consumer coping with disturbing thoughts?", scale: "coping", field: "q19" },
  { num: 20, text: "How well is the consumer coping with difficulty sleeping?", scale: "coping", field: "q20" },
  { num: 21, text: "How well is the consumer coping with external stressors (health problems, family conflicts, unemployment, tragedy in community)?", scale: "coping", field: "q21" },

  { header: "Think about how satisfied the consumer is with his/her life:" },
  { num: 22, text: "How does the consumer feel about the amount of freedom he/she has where he/she lives?", scale: "satisfaction", field: "q22" },
  { num: 23, text: "How does the consumer feel about having things to do that he/she enjoys (for example, going to the movies, sports events, hobbies)?", scale: "satisfaction", field: "q23" },
  { num: 24, text: "How does the consumer feel about the way he/she spends his/her free time?", scale: "satisfaction", field: "q24" },
  { num: 25, text: "How does the consumer feel about the amount of privacy he/she has where he/she lives?", scale: "satisfaction", field: "q25" },
  { num: 26, text: "How does the consumer feel about the amount of comfort and security he/she has where he/she lives?", scale: "satisfaction", field: "q26" },
  { num: 27, text: "How does the consumer feel about his/her access to health care?", scale: "satisfaction", field: "q27" },
  { num: 28, text: "How does the consumer feel about the neighborhood and residence where he/she lives?", scale: "satisfaction", field: "q28" },
  { num: 29, text: "How does the consumer feel about his/her access to reliable transportation?", scale: "satisfaction", field: "q29" },
  { num: 30, text: "How does the consumer feel about his/her relationships with family/significant others/friends?", scale: "satisfaction", field: "q30" },

  { header: "Other indicators:" },
  { num: 31, text: "To what extent do you believe the consumer's use of alcohol or street drugs interferes with his/her recovery from mental illness?", scale: "belief", field: "q31", optOut: "The consumer does not use alcohol or street drugs." },
  { num: 32, text: "To what extent do you believe the consumer takes his/her psychiatric medication as prescribed?", scale: "frequency", field: "q32", optOut: "Psychiatric medication has not been prescribed for the consumer." },
  { num: 33, text: "To what extent do you believe the consumer's psychiatric medication helps with his/her recovery from mental illness?", scale: "belief", field: "q33", optOut: "Psychiatric medication has not been prescribed for the consumer." },
];

/* ------------------------------------------------------------------ */
/*  Small presentational pieces                                        */
/* ------------------------------------------------------------------ */
const YesNoScale = ["Yes", "No"];

function Circle({ filled }) {
  return <span className={`opt-circle${filled ? " filled" : ""}`} />;
}

function OptionScale({ options, value, optOut }) {
  const v = norm(value);
  const allOptions = optOut ? [...options, optOut] : options;
  const matched = allOptions.some((o) => norm(o) === v);
  return (
    <div className="option-scale">
      {allOptions.map((opt) => (
        <label key={opt} className={`option-item${optOut === opt ? " option-item--optout" : ""}`}>
          <Circle filled={norm(opt) === v} />
          <span className="option-label">{opt}</span>
        </label>
      ))}
      {!matched && value && (
        <span className="option-item option-item--freeform">
          <Circle filled />
          <span className="option-label">{value}</span>
        </span>
      )}
    </div>
  );
}

function QuestionBlock({ item, responses }) {
  if (item.header) {
    return <div className="scale-header">{item.header}</div>;
  }
  const value = responses?.[item.field];
  return (
    <div className="question-block">
      <div className="question-text">
        <span className="q-num">{item.num}.</span> {item.text}
      </div>
      <OptionScale options={SCALE[item.scale]} value={value} optOut={item.optOut} />
    </div>
  );
}

function QuestionSet({ questions, responses }) {
  return (
    <div className="responses-list">
      {questions.map((item, i) => (
        <QuestionBlock key={item.field || item.header || i} item={item} responses={responses} />
      ))}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div className="meta-item">
      <span className="meta-label">{label}:</span>
      <span className="meta-value">{value || "-"}</span>
    </div>
  );
}

function InlineChoice({ label, value, options = YesNoScale }) {
  const v = norm(value);
  return (
    <div className="inline-choice">
      <span className="inline-choice-label">{label}</span>
      <span className="inline-choice-options">
        {options.map((opt) => (
          <label key={opt} className="option-item">
            <Circle filled={norm(opt) === v} />
            <span className="option-label">{opt}</span>
          </label>
        ))}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
export default function Cros3FormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentCros3Form, isLoading, error } = useSelector(
    (state) => state.cros3Forms
  );

  useEffect(() => {
    if (id) dispatch(fetchCros3Form(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentCros3Form) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentCros3Form, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading CROS-3 Form print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentCros3Form) return null;

  const d = currentCros3Form;

  const dischargeOptions = ["Within last 6 months", "Within 6 months to 1 year ago", "Over 1 year ago"];

  return (
    <>
      <style jsx global>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm 15mm 15mm 15mm;
          }
          body {
            background: #fff !important;
            color: #000 !important;
          }
          .no-print {
            display: none !important;
          }
          .page-break {
            page-break-before: always;
          }
        }

        * {
          box-sizing: border-box !important;
          font-family: "Times New Roman", Times, serif !important;
        }

        body {
          background: #fff;
          color: #000;
          font-size: 10pt !important;
          line-height: 1.4 !important;
        }

        .print-container {
          padding: 10px !important;
          width: 100% !important;
          max-width: 800px;
          margin: 0 auto;
        }

        .header-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 5px;
        }

        .header-logo img {
          height: 60px;
        }

        .header-text {
          text-align: center;
          margin-bottom: 20px;
        }

        .header-text h2 {
          font-size: 16pt !important;
          font-weight: bold !important;
          margin: 0 0 2px 0 !important;
          text-transform: uppercase;
        }

        .header-text h3 {
          font-size: 13pt !important;
          font-weight: bold !important;
          margin: 0 0 2px 0 !important;
          text-transform: uppercase;
          font-style: italic;
        }

        .section-title {
          font-size: 12pt !important;
          font-weight: bold !important;
          background-color: #f2f2f2;
          padding: 6px 10px;
          margin-top: 25px;
          margin-bottom: 15px;
          border-left: 4px solid #000;
          text-transform: uppercase;
        }

        .metadata-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 20px;
          margin-bottom: 18px;
          border: 1px solid #ccc;
          padding: 12px;
          background-color: #fafafa;
        }

        .meta-item {
          display: flex;
          font-size: 9.5pt;
        }

        .meta-label {
          font-weight: bold;
          min-width: 140px;
        }

        .meta-value {
          border-bottom: 1px dotted #000;
          flex-grow: 1;
          padding-left: 5px;
        }

        .responses-list {
          margin-top: 4px;
          margin-bottom: 15px;
        }

        .scale-header {
          font-style: italic;
          font-weight: bold;
          margin: 16px 0 8px 0;
          font-size: 9.5pt;
          border-bottom: 1px solid #999;
          padding-bottom: 3px;
        }
        .scale-header::before {
          content: "\\25BE  ";
        }

        .question-block {
          padding: 5px 0 8px 0;
          border-bottom: 1px dotted #ccc;
          font-size: 9.5pt;
        }

        .question-text {
          margin-bottom: 5px;
        }

        .q-num {
          font-weight: bold;
        }

        .option-scale {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 18px;
          padding-left: 18px;
        }

        .option-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 9pt;
          white-space: nowrap;
        }

        .option-item--optout {
          font-style: italic;
          color: #333;
        }

        .option-item--freeform {
          color: #333;
        }

        /*
         * Filled state uses a thick BORDER (not background-color) to draw
         * the solid dot. Browsers strip background-color/background-image
         * by default when printing (window.print()/Save-as-PDF) unless the
         * user manually enables "background graphics" — border color is
         * never stripped, so this renders correctly in print every time.
         * The print-color-adjust rules below are added as a second layer
         * of defense in case background fills are used anywhere else.
         */
        .opt-circle {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 1.3px solid #000;
          display: inline-block;
          flex-shrink: 0;
          box-sizing: border-box;
          background: transparent;
        }

        .opt-circle.filled {
          border-width: 5px;
          background: #000;
        }

        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }

        .inline-choice {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 10px 0;
          font-size: 9.5pt;
        }

        .inline-choice-label {
          font-weight: bold;
        }

        .inline-choice-options {
          display: flex;
          gap: 16px;
        }

        .staff-meta-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
          font-size: 9pt;
        }
        .staff-meta-table td {
          border: 1px solid #ccc;
          padding: 5px 8px;
          vertical-align: middle;
        }
        .staff-meta-table td:first-child {
          width: 55%;
        }

        .end-note {
          text-align: center;
          font-weight: bold;
          margin-top: 20px;
          font-style: italic;
        }
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
      `}</style>

      <PrintWrapper>
        <div className="print-container">
        {/* LOGO */}
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>

        {/* HEADER SECTION */}
        <div className="header-text">
          <h2>THE RECOVERY HOUSE (TRH)</h2>
          <h3>Consumer CROS 3.0 Questionnaire</h3>
        </div>

        {/* PART 1: CONSUMER CROS 3.0 */}
        <div className="metadata-grid">
          <MetaField
            label="Date filled out"
            value={`${d.consumer_details?.month || "--"} / ${d.consumer_details?.day || "--"} / ${d.consumer_details?.year || "--"}`}
          />
          <MetaField label="Organization ID" value={d.consumer_details?.organization_id} />
          <MetaField label="Agency ID" value={d.consumer_details?.agency_id} />
          <MetaField label="Program ID" value={d.consumer_details?.program_id} />
          <MetaField label="Staff ID" value={d.consumer_details?.staff_id} />
        </div>

        <div className="helper-text" style={{ fontStyle: "italic", marginBottom: 10, fontSize: "9pt" }}>
          If you are new to this agency, start at Question 6.
        </div>

        <QuestionSet questions={CONSUMER_QUESTIONS} responses={d.consumer_responses} />

        <InlineChoice label="Did anyone help you complete this form?" value={d.consumer_details?.help} />
        <InlineChoice label="Was this questionnaire translated into another language for you?" value={d.consumer_details?.translated} />

        <div className="end-note">The End. Thank You!</div>

        {/* PART 2: STAFF CROS 3.0 */}
        <div className="page-break"></div>
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>
        <div className="header-text">
          <h2>THE RECOVERY HOUSE (TRH)</h2>
          <h3>Staff CROS 3.0 Questionnaire</h3>
        </div>

        <div className="metadata-grid">
          <MetaField label="Primary DSM-IV" value={d.staff_details?.primary} />
          <MetaField label="Secondary DSM-IV" value={d.staff_details?.secondary} />
          <MetaField label="Tertiary DSM-IV" value={d.staff_details?.tertiary} />
        </div>

        <table className="staff-meta-table">
          <tbody>
            <tr>
              <td>1. Last hospital discharge was (leave blank if never hospitalized)</td>
              <td>
                <OptionScale options={dischargeOptions} value={d.staff_details?.last_discharge} />
              </td>
            </tr>
            <tr>
              <td>2. Does the consumer have a representative payee?</td>
              <td>
                <OptionScale options={YesNoScale} value={d.staff_details?.has_payee} />
              </td>
            </tr>
            <tr>
              <td>3. Is the consumer chronically homeless?</td>
              <td>
                <OptionScale options={YesNoScale} value={d.staff_details?.chronically_homeless} />
              </td>
            </tr>
            <tr>
              <td>4. Is the consumer new to this agency?</td>
              <td>
                <OptionScale options={YesNoScale} value={d.staff_details?.new_to_agency} />
              </td>
            </tr>
            <tr>
              <td>5. Is the consumer new to this agency?</td>
              <td>
                <OptionScale options={YesNoScale} value={d.staff_details?.new_to_agency_2} />
              </td>
            </tr>
          </tbody>
        </table>

        <QuestionSet questions={STAFF_QUESTIONS} responses={d.staff_responses} />

        <div className="end-note">The End. Thank You!</div>

        {/* PART 3: VIP CROS 3.0 */}
        <div className="page-break"></div>
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>
        <div className="header-text">
          <h2>THE RECOVERY HOUSE (TRH)</h2>
          <h3>VIP CROS 3.0 Questionnaire</h3>
          <div style={{ fontSize: "10pt", fontWeight: "bold", marginTop: 4 }}>
            for {d.vip_details?.consumer_name || "________________"}
          </div>
        </div>

        <div className="metadata-grid">
          <MetaField label="Organization ID" value={d.vip_details?.organization_id} />
          <MetaField label="Program ID" value={d.vip_details?.program_id} />
          <MetaField label="Staff ID" value={d.vip_details?.staff_id} />
          <MetaField label="Agency ID" value={d.vip_details?.agency_id} />
          <MetaField
            label="Date CROS Filled Out"
            value={d.vip_details?.filled_date ? new Date(d.vip_details.filled_date).toLocaleDateString("en-GB") : null}
          />
        </div>

        <div className="helper-text" style={{ fontStyle: "italic", marginBottom: 6, fontSize: "9pt" }}>
          Start here (completely fill only one circle for each of the following questions)
        </div>

        <InlineChoice
          label="Please indicate your relationship to the consumer:"
          value={d.vip_details?.relationship}
          options={["Spouse/Significant Other", "Other Relative", "Parent/Step-parent", "Friend", "Residential Facility Staff", "Other"]}
        />

        <QuestionSet questions={VIP_QUESTIONS} responses={d.vip_responses} />

        <div className="end-note">The End. Thank You!</div>
      </div>
      </PrintWrapper>
    </>
  );
}